import { ChangeEvent, useCallback, useEffect, useState } from 'react'
import { PageBanner } from 'cwp-components/PageBanner'
import useLegalDocumentTemplateFilters from 'common/providers/useLegalDocumentTemplateFilters'
import {
  useLegalDocumentTemplateSearch,
  SORT_OPTIONS,
} from 'common/hooks/useLegalDocumentTemplateSearch'
import useDocumentFilters from 'common/providers/useDocumentFilters'
import Constants from 'common/utils/constants'
import { BsSearch } from 'react-icons/bs'
import { TemplateCardSmall } from './components/TemplateCardSmall'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { InputField, SelectField } from 'cwp-components/forms'
import { useForm } from 'react-hook-form'

const LegalDocumentsPage = ({ pageSize = 10 }) => {
  const navigate = useNavigate()
  const {
    results,
    setSortOption,
    setDocumentFilterId,
    selectedType,
    loadMore,
    setSelectedType,
    hasNext,
    updateSearchValue,
    sortOption,
    documentFilterId,
    searchValue,
  } = useLegalDocumentTemplateSearch({ pageSize })
  const [searchParams, setSearchParams] = useSearchParams()
  const { legalDocumentTemplateFilters, getLegalDocumentTemplateFilters } =
    useLegalDocumentTemplateFilters()

  const { documentFilters, getDocumentFilters } = useDocumentFilters()
  const documentFilter = documentFilters?.find((f) => f.id === documentFilterId)

  useEffect(() => {
    getDocumentFilters({})
    getLegalDocumentTemplateFilters({
      is_template: Constants.isAvvokaEnabled,
    })
  }, [])

  const DEFAULT_FILTER_NAME = 'All Templates'
  const documentFilterOptions = [
    { id: 'default', name: DEFAULT_FILTER_NAME },
    ...(documentFilters ?? []),
  ].map((f) => ({
    value: f.name,
    label: f.name,
  }))

  const handleSearchInputChange = (e: ChangeEvent<HTMLInputElement>) => {
    setSearchParams(
      (prev) => {
        const search = e.target.value
        const updated = new URLSearchParams(prev)
        if (search) {
          updated.set('search', search)
        } else {
          updated.delete('search')
        }
        return updated
      },
      { replace: true },
    )
  }

  const onFilterChange = (e: ChangeEvent<HTMLSelectElement>) => {
    setSearchParams(
      (prev) => {
        const filter = e.target.value
        const updated = new URLSearchParams(prev)
        if (filter !== DEFAULT_FILTER_NAME) {
          updated.set('filter', filter)
        } else {
          updated.delete('filter')
        }
        return updated
      },
      { replace: true },
    )
  }

  const onSortChange = (e: ChangeEvent<HTMLSelectElement>) => {
    const value = e.target.value
    const option = SORT_OPTIONS.find((opt) => opt.value === value)
    setSearchParams(
      (prev) => {
        const updated = new URLSearchParams(prev)
        if (option) {
          updated.set('sort', option.value)
        } else {
          updated.delete('sort')
        }
        return updated
      },
      { replace: true },
    )
  }

  const onTypeClick = useCallback((id) => {
    setSearchParams(
      (prev) => {
        const updated = new URLSearchParams(prev)
        if (id) {
          updated.set('type', id)
        } else {
          updated.delete('type')
        }
        return updated
      },
      { replace: true },
    )
  }, [])

  useEffect(() => {
    const newSearchValue = searchParams.get('search') || ''
    const newSelectedType = searchParams.get('type') || undefined
    const newDocumentFilterName = searchParams.get('filter') || undefined
    const newDocumentFilter = documentFilters?.find(
      (f) => f.name?.toLowerCase() === newDocumentFilterName?.toLowerCase(),
    )

    const newSortOption =
      SORT_OPTIONS.find(
        (option) => option?.value === searchParams.get('sort'),
      ) ?? SORT_OPTIONS[0]

    if (searchValue !== newSearchValue) {
      updateSearchValue(newSearchValue)
    }
    if (selectedType !== newSelectedType) {
      setSelectedType(newSelectedType)
    }
    if (sortOption !== newSortOption) {
      setSortOption(newSortOption)
    }
    if (documentFilter?.name !== newDocumentFilterName) {
      setDocumentFilterId(newDocumentFilter?.id)
    }
  }, [searchParams, documentFilters])

  const {
    register,
    formState: { errors },
  } = useForm({
    mode: 'onBlur',
    reValidateMode: 'onChange',
  })

  return (
    <div>
      <PageBanner>LegalDocs</PageBanner>

      <div className='flex flex-col px-[27px] sm:px-[40px] md:px-[60px] lg:pl-[88px] pt-[50px] lg:pt-10 gap-[12px]'>
        <span className='font-medium md:font-bold text-base md:text-lg tracking-wide'>
          Create, sign and share in minutes
        </span>

        <div className='flex flex-col md:flex-row items-center gap-x-[10px] lg:gap-x-[40px] gap-y-[16px]'>
          <div className='md:w-4/12 flex items-center relative w-full'>
            <InputField
              required={false}
              register={register}
              icon={<BsSearch />}
              id='search'
              label='Search'
              placeholder='Search'
              value={searchParams.get('search')}
              onChange={handleSearchInputChange}
              className='h-12'
            />
          </div>
          <div className='md:w-8/12 lg:w-8/12 flex flex-col md:flex-row gap-3'>
            <div className='w-full md:w-4/12'>
              <SelectField
                required={false}
                id='FilterBy'
                label='Filter By'
                placeholder='Filter By'
                records={documentFilterOptions}
                selectClassName='!h-[40px]'
                value={
                  documentFilterOptions.find(
                    (o) => o.value === searchParams.get('filter'),
                  )?.label
                }
                onChange={onFilterChange}
              />
            </div>
            <div className='w-full md:w-4/12'>
              <SelectField
                required={false}
                id='SortBy'
                label='Sort By'
                placeholder='Sort By'
                records={SORT_OPTIONS}
                selectClassName='!h-[40px]'
                value={
                  SORT_OPTIONS.find((o) => o.value === searchParams.get('sort'))
                    ?.label
                }
                onChange={onSortChange}
              />
            </div>

            <div className='w-full md:w-4/12 h-full'>
              <div className='flex items-center gap-2 md:h-[40px]'>
                <button
                  disabled={searchParams.size === 0}
                  onClick={() => navigate('/legal-documents')}
                  className={`${
                    searchParams.size === 0
                      ? 'text-[#A0ABBE]'
                      : 'text=[#001A3D]'
                  } text-[14px] lg:text-[16px] font-medium leading-[24px] tracking-[0.4px] cursor-pointer`}
                >
                  Clear
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      <section className='flex flex-col items-center pb-[50px] px-[50px] sm:px-[70px] md:px-[100px] lg:px-[140px] xl:px-[175px] 2xl:px-[217px]'>
        <div className='flex flex-wrap w-full justify-center md:justify-start gap-x-[12px] md:gap-x-[16px] pt-[70px] pb-[16px] md:py-[16px]'>
          {legalDocumentTemplateFilters?.types.map(({ label, id }) => (
            <div key={label}>
              <button
                className={`cursor-pointer whitespace-nowrap mt-4 select-none font-medium tracking-wide ${
                  selectedType === id && 'border-[#1C7D93] border-b-[4px]'
                }`}
                onClick={() => onTypeClick(id)}
              >
                {label}
              </button>
            </div>
          ))}
        </div>

        <div className='grid gap-6 grid-cols-[repeat(auto-fit,minmax(273px,1fr))]'>
          {results.map((legalDocuments, idx) => (
            <TemplateCardSmall
              key={legalDocuments.title + idx}
              path={`/legal-documents/${legalDocuments.id}`}
              title={legalDocuments.title}
              description={legalDocuments.description}
            />
          ))}
        </div>

        {!!hasNext && (
          <div className='flex w-full justify-end mt-5 mb-[70px]'>
            <a
              type='button'
              className='text-[14px] underline font-medium cursor-pointer'
              onClick={loadMore}
            >
              Show more
            </a>
          </div>
        )}
      </section>
    </div>
  )
}

export default LegalDocumentsPage
